import { colors } from "../../constants/colors";
import featuredIcon from "../images/featured-icon.svg";
import featuredIconAS from "../images/featured-icon-as.svg";
import featuredIconCHOICE from "../images/featured-icon-choice.svg";
import checkIcon from "../images/check-white.svg";
import checkIconGray from "../images/check-gray.svg";
import { APP_ENVIRONMENT } from '../../constants/appConstants';

const deepMerge = (base, overrides) => {
  const result = { ...base };
  for (const key in overrides) {
    result[key] =
      overrides[key] && typeof overrides[key] === "object" && !Array.isArray(overrides[key]) && base[key]
        ? deepMerge(base[key], overrides[key])
        : overrides[key];
  }
  return result;
};

const { AA, UC, AS, CHOICE, AM, AC } = {
  AA: {
    primary: colors.darkBlue, // #0860C4
    primaryHover: colors.primaryHover, // #064D9F
    primaryDisabled: colors.primary200Blue, // #ADCAEB
    primaryLight: colors.primary100Blue, // #CEDFF3
    primaryLighter: colors.primary50Blue, // #F5FAFF
    primaryDark: colors.primary700Blue, // #0860C4
    focusRing: colors.babyBlue, // #B3D7F2
    navHoverShadow: colors.lightBlue, // #E6F2FB
    selectedBg: colors.primary25AS, // #F5F9FD
    toggleActive: colors.primary600Blue, // #0860C4
    toggleActiveHover: colors.primary700Blue, // #0860C4
  },
  UC: {
    primary: colors.darkerGrey, // #353535
    primaryHover: colors.mediumBlack, // #141414
    primaryDisabled: colors.disabledBlack, // #BBBBBB
    primaryLight: colors.primary100Grey, // #DDDDDD
    primaryLighter: colors.silverGrey, // #EEEEEE
    primaryDark: colors.mediumBlack, // #141414
    focusRing: colors.paleGrey, // #98A2B3
    navHoverShadow: colors.silverGrey, // #EEEEEE
    selectedBg: colors.primary25UC, // #F6F6F6
    toggleActive: colors.darkerGrey, // #353535
    toggleActiveHover: colors.mediumBlack, // #141414
  },
  AS: {
    primary: colors.blueAS, // #0074C8
    primaryHover: colors.hoverAS, // #054687
    primaryDisabled: colors.disabledAS, // rgba(0,116,200,0.20)
    primaryLight: colors.primary100Blue, // #CEDFF3
    primaryLighter: colors.primary50Blue, // #F5FAFF
    primaryDark: colors.hoverAS, // #054687
    focusRing: colors.babyBlue, // #B3D7F2
    navHoverShadow: colors.veryLightGrey, // #F2F4F7
    selectedBg: colors.primary25AS, // #F5F9FD
    toggleActive: colors.blueAS, // #0074C8
    toggleActiveHover: colors.blueAS, // #0074C8
  },
  CHOICE: {
    primary: colors.boralaOrange, // #FFC72C
    primaryHover: colors.boralaOrangeHover, // #FFC013
    primaryDisabled: colors.lighterOrange, // #FFF6D6
    primaryLight: colors.veryLightOrange, // #FFFAEB
    primaryLighter: colors.disabledOrange, // #FFEDB0
    primaryDark: colors.choiceGray600, // #535862
    focusRing: colors.lightOrange, // #FDEAD7
    navHoverShadow: colors.veryLightGrey, // #F2F4F7
    selectedBg: colors.veryLightOrange, // #FFFAEB
    toggleActive: colors.choiceGray600, // #535862
    toggleActiveHover: colors.choiceGray600, // #535862
  },
  AM: {
    primary: colors.amPrimaryBtn,
    primaryHover: colors.amPrimaryBtnHover,
    primaryDisabled: colors.amPrimaryBtnDisabled,
    primaryLight: colors.primary50Blue,
    primaryLighter: colors.primary200Blue,
    primaryDark: colors.choiceGray600, // #535862
    focusRing: colors.lightOrange, // #FDEAD7
    navHoverShadow: colors.veryLightGrey, // #F2F4F7
    selectedBg: colors.veryLightOrange, // #FFFAEB
    toggleActive: colors.choiceGray600, // #535862
    toggleActiveHover: colors.choiceGray600, // #535862
  },
  AC: {
    primary: colors.deepBlue,
    primaryHover: colors.deeperBlue,
    primaryDisabled: colors.disabledAC,
    primaryLight: colors.white,
    primaryLighter: colors.disabledOrange,
    primaryDark: colors.choiceGray600,
    focusRing: colors.lightOrange,
    navHoverShadow: colors.darkBlack,
    selectedBg: colors.white,
    toggleActive: colors.choiceGray600,
    toggleActiveHover: colors.choiceGray600,
  },
};

const baseTheme = {
  fontFamily: "Inter",
  navigation: {
    backgroundColor: colors.white,
    borderRight: "1px solid " + colors.lightGrey,
    li: {
      default: { color: colors.grey },
      disabled: { color: colors.darkGrey },
    },
    profileAvatar: { color: colors.white, border: "none" },
    mobile: { backgroundColor: colors.white },
    companyName: { color: colors.mediumGrey },
    color: colors.grey,
  },
  buttons: {
    default: {
      color: colors.grey,
      backgroundColor: colors.white,
      border: "1px solid" + colors.darkGrey,
      hover: { color: colors.grey, backgroundColor: colors.brightBlue },
    },
    secondary: {
      color: colors.grey,
      backgroundColor: colors.white,
      border: "1px solid" + colors.darkGrey,
      hover: { color: colors.grey, backgroundColor: colors.brightBlue },
    },
    danger: { color: colors.white, backgroundColor: colors.error600, border: "1px solid" + colors.error600 },
    disabledIcon: { color: colors.white },
  },
  notification: {
    background: colors.white,
    border: "1px solid " + colors.veryLightGrey,
    boxShadow: "0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)",
    titleColor: colors.lightBlack,
    descriptionColor: colors.lightBlack,
    color: colors.mediumGrey,
  },
  tabs: {
    borderColor: colors.lightGrey,
    color: colors.inactiveGrey,
    numberBadge: { backgroundColor: colors.primary50Blue },
    titleColor: colors.lightBlack,
    descriptionColor: colors.mediumGrey,
    containerTitleColor: colors.grey,
    containerDescriptionColor: colors.mediumGrey,
  },
  spinners: {
    general: colors.mediumGrey,
    primary: colors.white,
    default: colors.white,
    danger: colors.white,
  },
  modal: {
    border: "4px solid " + colors.white,
    optionHover: { border: "1px solid " + colors.primary300Blue, backgroundColor: colors.primary100Blue },
    selectedOption: {
      icon: { stroke: colors.darkBlue, fill: colors.primary100Blue },
    },
  },
  toggle: {
    thumbColor: colors.white,
    unchecked: {
      backgroundColor: `${colors.veryLightGrey} !important`,
      hover: { backgroundColor: `${colors.lightGrey} !important` },
    },
  },
  flatpickr: {
    background: colors.brightBlue,
    borderColor: colors.brightBlue,
    disabled: colors.darkGrey,
    selectDates: { color: colors.inactiveGrey },
    popoverDiv: { backgroundColor: colors.white, border: `1px solid ${colors.lightGrey}`, color: colors.grey },
    selectedDate: { border: "1px solid " + colors.darkGrey, color: colors.lightBlack },
    active: { backgroundColor: colors.brightBlue },
    divider: { borderBottom: `1px solid ${colors.lightGrey}` },
    flatpickrWeekday: { color: colors.grey },
    today: {
      color: colors.white,
      after: { backgroundColor: colors.veryLightGrey },
    },
    selected: { hover: { color: colors.grey } },
    day: {
      colorDefault: colors.white,
      color: colors.inactiveGrey,
      backgroundColor: colors.white,
      today: { backgroundColor: colors.veryLightGrey, hover: { color: colors.grey } },
      inRange: { boxShadow: `0px 0 0 ${colors.brightBlue}, 5px 0 0 ${colors.brightBlue}` },
    },
    dayContainer: { borderLeft: `1px solid ${colors.lightGrey}` },
    currentMonth: { color: colors.grey },
    monthDay: { borderRight: `1px solid ${colors.borderGrey}` },
    weekday: { boxShadow: `-1px 0 0 ${colors.borderGrey}` },
  },
  tooltip: {
    styleOne: {
      color: colors.grey,
      backgroundColor: colors.veryLightGrey,
      pathBackgroundColor: colors.paleGrey,
    },
  },
  progress: { backgroundColor: colors.lightGrey },
  navigationIcon: { stroke: colors.inactiveGrey },
  icon: {
    stroke: colors.grey,
    border: "1px solid" + colors.lightGrey,
    borderRadius: "10px",
    boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
    backgroundColor: "transparent",
    checkIcon: `url(${checkIcon})`,
  },
  filterText: { filterTextColor: colors.lightBlack, filterTextSize: "16px" },
  discounts: {
    tierLogoActive: { backgroundColor: colors.success100, color: colors.grayBlue600, borderColor: colors.success50 },
    progressBarActive: { color: colors.success600 },
  },
  pendingInvitation: {
    buttons: {
      default: {
        backgroundColor: colors.white,
        color: colors.grey,
        hover: { backgroundColor: colors.lightGrey, color: colors.grey },
      },
    },
    icon: { border: "none", backgroundColor: "transparent", stroke: colors.white },
  },
  field: {
    borderColor: colors.darkGrey,
    borderColorFocus: colors.black,
    borderColorHover: colors.darkGrey,
    borderColorDisabled: "rgba(118, 118, 118, 0.3)",
    borderColorMenu: colors.lightGrey,
    borderColorError: colors.mediumRed,
    placeholderColor: colors.inactiveGrey,
    placeholderColorDisabled: colors.inactiveGrey,
    textColor: colors.lightBlack,
    textColorDisabled: colors.inactiveGrey,
    backgroundColor: colors.white,
    backgroundColorDisabled: "rgba(239, 239, 239, 0.3)",
    backgroundColorHover: colors.brightBlue,
    backgroundColorFocus: colors.white,
    labelColor: colors.grey,
  },
  pill: { border: "none" },
  reportsInfoIcon: { color: colors.choiceIconGray },
  privacyOptions: { textColor: colors.mediumGrey, borderColor: colors.gray200, backgroundColor: colors.white, backgroundColorHover: colors.brightBlue, textHover: colors.gray50 },
};

const themeOverrides = {
  AA: deepMerge(baseTheme, {
    navigation: {
      li: {
        hover: { boxShadow: '0px 0px 0px 4px ' + AA.navHoverShadow, color: AA.primary },
        active: { color: AA.primary, backgroundColor: AA.primaryLighter },
      },
      profileAvatar: { backgroundColor: colors.greyNeutral },
    },
    buttons: {
      primary: {
        color: colors.white,
        backgroundColor: AA.primary,
        border: '1px solid' + AA.primary,
        hover: { color: colors.white, backgroundColor: AA.primaryHover, border: '1px solid' + AA.primary },
      },
      secondary: {
        color: AA.primaryDark,
        backgroundColor: AA.primaryLighter,
        border: '1px solid' + AA.primaryLighter,
        hover: { color: AA.primaryDark, backgroundColor: AA.primaryLight },
      },
      disabled: {
        color: colors.white,
        backgroundColor: `${AA.primaryDisabled} !important`,
        border: `1px solid ${AA.primaryDisabled}`,
      },
      badge: { backgroundColor: AA.primaryLighter, color: AA.primary },
      defaultLink: { color: AA.primary },
    },
    tabs: {
      activeColor: AA.primary,
      borderBottom: '2px solid ' + AA.primary,
    },
    colors: { primary: AA.primary },
    modal: {
      backgroundColor: AA.primary,
      outline: '2px solid ' + AA.primary,
      boxShadow: '0 0 0 0.25rem ' + AA.focusRing,
      selectedOption: {
        text: { color: AA.primary },
        backgroundColor: AA.selectedBg,
        border: '2px solid ' + AA.primary,
      },
    },
    toggle: {
      color: colors.grey,
      checked: {
        backgroundColor: `${AA.toggleActive} !important`,
        hover: { backgroundColor: `${AA.toggleActiveHover} !important` },
      },
    },
    flatpickr: {
      today: { after: { default: { backgroundColor: AA.toggleActive } } },
      selected: { backgroundColor: AA.toggleActive },
    },
    tooltip: {
      styleOne: {
        hover: { backgroundColor: AA.primaryLight, pathBackgroundColor: AA.primary },
      },
      styleTwo: {
        color: AA.primaryDark,
        backgroundColor: AA.primaryLighter,
        pathBackgroundColor: AA.primary,
        hover: { backgroundColor: AA.primaryLight, pathBackgroundColor: AA.primary },
      },
    },
    progress: { accentColor: AA.primary },
    navigationIcon: { hover: { stroke: AA.primary } },
    text: { themeColor: AA.primary },
    discounts: {
      tierLogo: {
        backgroundColor: AA.primaryLight,
        color: AA.toggleActive,
        borderColor: AA.primaryLighter,
      },
      progressBar: { color: AA.toggleActive },
    },
    uploadModal: { backgroundImage: `url(${featuredIcon})`, linkColor: AA.primary },
    pendingInvitation: {
      backgroundColor: AA.primary,
      color: colors.white,
      checkIconColor: AA.primary,
      buttons: {
        primary: {
          backgroundColor: AA.primaryLighter,
          color: AA.primary,
          border: '1px solid' + colors.darkGrey,
          hover: {
            backgroundColor: AA.primaryLight,
            color: AA.primary,
            boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
            border: `1px solid ${colors.darkGrey}`,
          },
          focus: {
            background: AA.primaryLighter,
            color: AA.primary,
            border: `1px solid ${colors.darkGrey}`,
          },
        },
      },
    },
    checkbox: {
      border: `1px solid ${AA.primary}`,
      backgroundColor: colors.white,
      iconColor: AA.primary,
    },
    field: { borderColorActive: AA.primary },
    inviteModal: { linkColor: AA.primary },
  }),

  UC: deepMerge(baseTheme, {
    navigation: {
      li: {
        hover: { boxShadow: '0px 0px 0px 4px ' + UC.navHoverShadow, color: UC.primary },
        active: { color: UC.primary, backgroundColor: UC.primaryLighter },
      },
      profileAvatar: { backgroundColor: colors.greyNeutral },
    },
    buttons: {
      primary: {
        color: colors.white,
        backgroundColor: UC.primary,
        border: '1px solid' + UC.primary,
        hover: { color: colors.white, backgroundColor: UC.primaryHover, border: '1px solid' + UC.primary },
      },
      secondary: {
        color: colors.grey,
        backgroundColor: colors.white,
        border: '1px solid' + colors.darkGrey,
        hover: { color: colors.grey, backgroundColor: colors.brightBlue },
      },
      disabled: {
        color: colors.white,
        backgroundColor: `${UC.primaryDisabled} !important`,
        border: `1px solid ${UC.primaryDisabled}`,
      },
      badge: { backgroundColor: UC.primaryLighter, color: UC.primary },
      defaultLink: { color: UC.primary },
    },
    tabs: {
      activeColor: UC.primary,
      borderBottom: '2px solid ' + UC.primary,
    },
    colors: { primary: UC.primary },
    modal: {
      backgroundColor: UC.primary,
      outline: '2px solid ' + UC.primary,
      boxShadow: '0 0 0 0.25rem ' + UC.focusRing,
      optionHover: { border: '1px solid ' + UC.primaryLight, backgroundColor: UC.primaryLight },
      selectedOption: {
        text: { color: UC.primary },
        backgroundColor: UC.selectedBg,
        border: '2px solid ' + UC.primary,
      },
    },
    toggle: {
      color: colors.grey,
      checked: {
        backgroundColor: `${UC.toggleActive} !important`,
        hover: { backgroundColor: `${UC.toggleActiveHover} !important` },
      },
    },
    flatpickr: {
      today: { after: { default: { backgroundColor: UC.toggleActive } } },
      selected: { backgroundColor: UC.toggleActive },
    },
    tooltip: {
      styleOne: {
        pathBackgroundColor: colors.greyLight,
        hover: { backgroundColor: colors.lightGrey, pathBackgroundColor: colors.grey },
      },
      styleTwo: {
        color: UC.primary,
        backgroundColor: UC.primaryLighter,
        pathBackgroundColor: colors.greyLight,
        hover: {
          backgroundColor: colors.primary100Grey,
          pathBackgroundColor: colors.primary500Grey,
        },
      },
    },
    progress: { accentColor: UC.primary },
    navigationIcon: { hover: { stroke: colors.primary500Grey } },
    text: { themeColor: UC.primary },
    discounts: {
      tierLogo: {
        backgroundColor: UC.primaryLight,
        color: UC.primary,
        borderColor: UC.primaryLighter,
      },
      progressBar: { color: UC.primary },
    },
    uploadModal: { backgroundImage: `url(${featuredIcon})`, linkColor: UC.primaryHover },
    pendingInvitation: {
      backgroundColor: colors.darkBlue,
      color: colors.white,
      checkIconColor: colors.darkBlue,
      buttons: {
        primary: {
          backgroundColor: colors.primary50Blue,
          color: colors.darkBlue,
          border: '1px solid' + colors.darkGrey,
          hover: {
            backgroundColor: colors.primary75Blue,
            color: colors.darkBlue,
            boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
            border: `1px solid ${colors.darkGrey}`,
          },
          focus: {
            background: colors.primary50Blue,
            color: colors.darkBlue,
            border: `1px solid ${colors.darkGrey}`,
          },
        },
      },
    },
    checkbox: {
      border: `1px solid ${colors.darkBlue}`,
      backgroundColor: colors.white,
      iconColor: colors.darkBlue,
    },
    field: { borderColorActive: colors.darkBlue },
    inviteModal: { linkColor: colors.darkBlue },
  }),

  AS: deepMerge(baseTheme, {
    navigation: {
      backgroundColor: colors.brightBlue,
      li: {
        hover: { boxShadow: '0px 0px 0px 4px ' + AS.navHoverShadow, color: AS.primaryHover },
        active: { color: AS.primaryHover, backgroundColor: colors.veryLightGrey },
        disabled: { color: colors.inactiveGrey },
      },
      profileAvatar: { backgroundColor: colors.primary400Blue },
    },
    buttons: {
      primary: {
        color: colors.white,
        backgroundColor: AS.primary,
        border: '1px solid' + AS.primary,
        hover: { color: colors.white, backgroundColor: AS.primaryHover, border: '1px solid' + AS.primary },
      },
      secondary: {
        color: AS.primary,
        backgroundColor: AS.navHoverShadow,
        border: '1px solid' + AS.navHoverShadow,
        hover: { color: AS.primaryDark, backgroundColor: AS.primaryLight },
      },
      disabled: {
        color: colors.white,
        backgroundColor: `${AS.primaryDisabled} !important`,
        border: `1px solid ${AS.primaryDisabled}`,
      },
      badge: { backgroundColor: AS.primaryLighter, color: AS.primaryHover },
      defaultLink: { color: AS.primaryHover },
    },
    tabs: {
      activeColor: AS.primaryHover,
      borderBottom: '2px solid ' + AS.primaryHover,
    },
    colors: { primary: AS.primary },
    modal: {
      backgroundColor: AS.primary,
      outline: '2px solid ' + AS.primary,
      boxShadow: '0 0 0 0.25rem ' + AS.focusRing,
      selectedOption: {
        text: { color: AS.primaryHover },
        backgroundColor: AS.selectedBg,
        border: '2px solid ' + colors.primary300Blue,
      },
    },
    toggle: {
      color: colors.gray700,
      checked: {
        backgroundColor: `${AS.toggleActive} !important`,
        hover: { backgroundColor: `${AS.toggleActiveHover} !important` },
      },
    },
    flatpickr: {
      today: { after: { default: { backgroundColor: AS.toggleActive } } },
      selected: { backgroundColor: AS.toggleActive },
    },
    tooltip: {
      styleOne: {
        hover: { backgroundColor: colors.lightGrey, pathBackgroundColor: colors.grey },
      },
      styleTwo: {
        color: AS.primaryHover,
        backgroundColor: AS.primaryLighter,
        pathBackgroundColor: colors.white,
        hover: { backgroundColor: AS.primaryDisabled, pathBackgroundColor: AS.primary },
      },
    },
    progress: { accentColor: AS.primary },
    navigationIcon: { hover: { stroke: AS.primary } },
    text: { themeColor: AS.primaryHover },
    discounts: {
      tierLogo: {
        backgroundColor: AS.primaryLight,
        color: AS.primary,
        borderColor: AS.primaryLighter,
      },
      progressBar: { color: AS.primary },
    },
    uploadModal: { backgroundImage: `url(${featuredIconAS})`, linkColor: AS.primaryHover },
    pendingInvitation: {
      backgroundColor: colors.darkBlue,
      color: colors.white,
      checkIconColor: colors.darkBlue,
      buttons: {
        primary: {
          backgroundColor: colors.primary50Blue,
          color: colors.darkBlue,
          border: '1px solid' + colors.darkGrey,
          hover: {
            backgroundColor: colors.primary75Blue,
            color: colors.darkBlue,
            boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
            border: `1px solid ${colors.darkGrey}`,
          },
          focus: {
            background: colors.primary50Blue,
            color: colors.darkBlue,
            border: `1px solid ${colors.darkGrey}`,
          },
        },
      },
    },
    checkbox: {
      border: `1px solid ${colors.darkBlue}`,
      backgroundColor: colors.white,
      iconColor: colors.darkBlue,
    },
    field: { borderColorActive: colors.darkBlue },
    inviteModal: { linkColor: AS.primary },
  }),

  CHOICE: deepMerge(baseTheme, {
    navigation: {
      backgroundColor: colors.choiceGray100,
      li: {
        hover: {
          boxShadow: '0px 0px 0px 4px ' + CHOICE.navHoverShadow,
          color: colors.lighterBlack,
        },
        active: { color: colors.lighterBlack, backgroundColor: colors.choiceGray200 },
        disabled: { color: colors.inactiveGrey },
      },
      profileAvatar: { backgroundColor: CHOICE.primary, color: colors.lighterBlack },
    },
    buttons: {
      primary: {
        color: colors.lighterBlack,
        backgroundColor: CHOICE.primary,
        border: '2px solid' + CHOICE.primary,
        hover: {
          color: colors.lighterBlack,
          backgroundColor: CHOICE.primaryHover,
          border: '2px solid' + CHOICE.primary,
        },
      },
      secondary: {
        color: CHOICE.primary,
        backgroundColor: CHOICE.primaryLight,
        border: '1px solid' + CHOICE.primaryLight,
        hover: { color: colors.lighterBlack, backgroundColor: colors.white },
      },
      default: {
        color: colors.gray700,
        border: '1px solid' + colors.gray300,
        hover: { color: colors.gray700, backgroundColor: colors.gray50 },
      },
      disabled: {
        color: colors.choiceIconGray,
        backgroundColor: `${CHOICE.primaryDisabled} !important`,
        border: `2px solid ${CHOICE.primaryLighter}`,
      },
      badge: { backgroundColor: CHOICE.primary, color: colors.lighterBlack },
      defaultLink: { color: colors.gray700 },
      disabledIcon: { color: colors.choiceIconGray },
    },
    tabs: {
      color: colors.gray500,
      activeColor: colors.gray700,
      borderBottom: '2px solid ' + colors.gray700,
      titleColor: colors.choiceGray900,
      descriptionColor: colors.choiceGray600,
      containerTitleColor: colors.gray700,
      containerDescriptionColor: colors.choiceGray600,
    },
    spinners: {
      general: colors.choiceGray600,
      primary: colors.choiceGray600,
      default: colors.choiceGray600,
      danger: colors.choiceGray600,
    },
    colors: { primary: CHOICE.primary },
    modal: {
      backgroundColor: CHOICE.primary,
      outline: '2px solid ' + CHOICE.primary,
      boxShadow: '0 0 0 0.25rem ' + CHOICE.focusRing,
      optionHover: { border: '1px solid ' + CHOICE.primary, backgroundColor: CHOICE.primaryLight },
      selectedOption: {
        text: { color: colors.gray700 },
        backgroundColor: CHOICE.selectedBg,
        border: '2px solid ' + CHOICE.primary,
        hoverBackgroundColor: CHOICE.primary,
        icon: { stroke: colors.choiceGray600, fill: CHOICE.primary },
      },
    },
    toggle: {
      color: colors.gray700,
      checked: {
        backgroundColor: `${CHOICE.toggleActive} !important`,
        hover: { backgroundColor: `${CHOICE.toggleActiveHover} !important` },
      },
    },
    flatpickr: {
      selectedDate: { border: '1px solid ' + colors.gray500 },
      today: { after: { default: { backgroundColor: CHOICE.primary } } },
      selected: { backgroundColor: CHOICE.primary },
    },
    tooltip: {
      styleOne: {
        hover: { backgroundColor: colors.lightGrey, pathBackgroundColor: colors.grey },
      },
      styleTwo: {
        backgroundColor: CHOICE.primaryLighter,
        color: colors.gray700,
        pathBackgroundColor: colors.gray700,
        hover: { backgroundColor: CHOICE.primaryDisabled, pathBackgroundColor: colors.gray700 },
      },
    },
    progress: { accentColor: CHOICE.primary, backgroundColor: colors.gray200 },
    navigationIcon: { stroke: colors.choiceGray600, hover: { stroke: colors.gray700 } },
    icon: {
      stroke: colors.gray700,
      border: '1px solid' + CHOICE.primaryDisabled,
      borderRadius: '50%',
      boxShadow: 'none',
      backgroundColor: CHOICE.primary,
      checkIcon: `url(${checkIconGray})`,
    },
    text: { themeColor: CHOICE.primary },
    discounts: {
      tierLogo: {
        backgroundColor: CHOICE.primaryLight,
        color: CHOICE.primary,
        borderColor: CHOICE.primaryLighter,
      },
      progressBar: { color: CHOICE.primary },
    },
    uploadModal: { backgroundImage: `url(${featuredIconCHOICE})`, linkColor: AS.primaryHover },
    pendingInvitation: {
      backgroundColor: colors.gray50,
      color: colors.gray700,
      checkIconColor: colors.grey,
      buttons: {
        default: {
          hover: { background: colors.gray50 },
        },
        primary: {
          backgroundColor: CHOICE.primary,
          color: colors.lighterBlack,
          border: 'none',
          hover: {
            background: CHOICE.primary,
            backgroundColor: CHOICE.primary,
            color: colors.lighterBlack,
            border: `2px solid ${CHOICE.primary}`,
          },
          focus: { background: CHOICE.primary, color: colors.lighterBlack },
        },
      },
      icon: {
        border: '1px solid' + CHOICE.primaryDisabled,
        backgroundColor: CHOICE.primary,
        stroke: colors.gray700,
      },
    },
    checkbox: {
      border: `1px solid ${CHOICE.primary}`,
      backgroundColor: CHOICE.primary,
      iconColor: colors.choiceGray600,
    },
    field: {
      borderColor: colors.darkGrey,
      borderColorFocus: colors.black,
      borderColorActive: colors.choiceGray600,
      borderColorHover: colors.darkGrey,
      borderColorDisabled: 'rgba(118, 118, 118, 0.3)',
      borderColorMenu: colors.lightGrey,
      placeholderColor: colors.inactiveGrey,
      placeholderColorDisabled: colors.inactiveGrey,
      textColor: colors.gray900,
      textColorDisabled: colors.gray500,
      backgroundColorDisabled: colors.gray50,
      backgroundColorHover: colors.gray50,
      labelColor: colors.lighterBlack,
    },
    pill: { border: `1px solid ${CHOICE.primary}` },
    inviteModal: { linkColor: AS.primary },
  }),

  AM: deepMerge(baseTheme, {
    navigation: {
      backgroundColor: colors.amNavBackground,
      color: colors.primary25AS,
      li: {
        default: { color: colors.primary25AS },
        hover: { boxShadow: '0px 0px 0px 4px ' + AM.navHoverShadow, color: colors.amNavActiveFg },
        active: { color: colors.amNavActiveFg },
        disabled: { color: colors.inactiveGrey },
      },
      profileAvatar: { backgroundColor: colors.pink600, color: colors.white },
      mobile: { backgroundColor: colors.primary700AM },
      companyName: { color: colors.primary200Blue },
    },
    buttons: {
      primary: {
        color: colors.white,
        backgroundColor: AM.primary,
        border: '1px solid' + AM.primary,
        hover: { color: colors.white, backgroundColor: AM.primaryHover, border: '1px solid' + AM.primaryHover },
      },
      secondary: {
        color: colors.grey,
        backgroundColor: colors.amSecondaryBtn,
        border: '1px solid' + colors.amSecondaryBtn,
        hover: {
          color: colors.white,
          backgroundColor: colors.amPrimaryBtnHover,
          border: '1px solid' + colors.amPrimaryBtnHover,
        },
      },
      default: {
        color: colors.grey,
        backgroundColor: colors.amSecondaryBtn,
        border: '1px solid' + colors.amSecondaryBtn,
        hover: {
          color: colors.white,
          backgroundColor: colors.amPrimaryBtnHover,
          border: '1px solid' + colors.amPrimaryBtnHover,
        },
      },
      disabled: {
        color: colors.white,
        backgroundColor: `${colors.amPrimaryBtnDisabled} !important`,
        border: `1px solid ${colors.amPrimaryBtnDisabled}`,
      },
      badge: { backgroundColor: colors.primary50Blue, color: AM.primary },
      defaultLink: { color: colors.gray700 },
    },
    tabs: {
      color: colors.inactiveGrey,
      activeColor: colors.primary700AM,
      borderBottom: '2px solid ' + colors.gray700,
      titleColor: colors.choiceGray900,
      descriptionColor: colors.choiceGray600,
      containerTitleColor: colors.gray700,
      containerDescriptionColor: colors.choiceGray600,
    },
    spinners: {
      general: colors.choiceGray600,
      primary: colors.choiceGray600,
      default: colors.choiceGray600,
      danger: colors.choiceGray600,
    },
    colors: { primary: AM.primary },
    modal: {
      backgroundColor: AM.primary,
      outline: '2px solid ' + AM.primary,
      boxShadow: '0 0 0 0.25rem ' + AM.focusRing,
      optionHover: { border: '1px solid ' + colors.primary500Blue, backgroundColor: AM.primaryLight },
      selectedOption: {
        text: { color: colors.primary500Blue },
        backgroundColor: AM.primaryLight,
        border: '2px solid ' + colors.primary500Blue,
        hoverBackgroundColor: AM.primary,
        icon: { stroke: colors.choiceGray600, fill: AM.primary },
      },
    },
    toggle: {
      color: colors.gray700,
      checked: {
        backgroundColor: `${AM.primary} !important`,
        hover: { backgroundColor: `${AM.toggleActiveHover} !important` },
      },
    },
    flatpickr: {
      selectedDate: { border: '1px solid ' + colors.gray500 },
      today: { after: { default: { backgroundColor: AM.primary } } },
      selected: { backgroundColor: AM.primary },
    },
    tooltip: {
      styleOne: {
        hover: { backgroundColor: colors.lightGrey, pathBackgroundColor: colors.grey },
      },
      styleTwo: {
        backgroundColor: colors.primary50Blue,
        color: AM.primary,
        pathBackgroundColor: AM.primary,
        hover: { backgroundColor: colors.primary50Blue, pathBackgroundColor: AM.primary },
      },
    },
    progress: { accentColor: AM.primary, backgroundColor: colors.gray200 },
    navigationIcon: { stroke: colors.primary25AS, hover: { stroke: colors.amNavActiveFg } },
    icon: {
      stroke: colors.grey,
      border: '1px solid' + colors.lightGrey,
      borderRadius: '10px',
      boxShadow: 'none',
      backgroundColor: colors.white,
      checkIcon: `url(${checkIconGray})`,
    },
    text: { themeColor: AM.primary },
    discounts: {
      tierLogo: {
        backgroundColor: AM.primaryLight,
        color: AM.primary,
        borderColor: AM.primaryLighter,
      },
      progressBar: { color: AM.primary },
    },
    uploadModal: { backgroundImage: `url(${featuredIconCHOICE})`, linkColor: AS.primaryHover }, //todo: adjust this
    pendingInvitation: {
      backgroundColor: colors.gray50,
      color: colors.gray700,
      checkIconColor: colors.grey,
      buttons: {
        default: {
          hover: { background: colors.gray50 },
        },
        primary: {
          backgroundColor: colors.amSecondaryBtn,
          color: colors.grey,
          border: `1px solid ${colors.amSecondaryBtn}`,
          hover: {
            background: colors.amPrimaryBtnHover,
            backgroundColor: colors.amPrimaryBtnHover,
            color: colors.white,
            border: `1px solid ${colors.amPrimaryBtnHover}`,
          },
          focus: {
            background: colors.amSecondaryBtn,
            color: colors.grey,
            border: `1px solid ${colors.amSecondaryBtn}`,
          },
        },
      },
      icon: {
        border: '1px solid' + AM.primaryDisabled,
        backgroundColor: AM.primary,
        stroke: colors.white,
      },
    },
    checkbox: {
      border: `1px solid ${colors.primary500Blue}`,
      backgroundColor: colors.primaryLight,
      iconColor: colors.primary500Blue,
    },
    field: {
      borderColor: colors.darkGrey,
      borderColorFocus: colors.black,
      borderColorActive: colors.choiceGray600,
      borderColorHover: colors.darkGrey,
      borderColorDisabled: 'rgba(118, 118, 118, 0.3)',
      borderColorMenu: colors.lightGrey,
      placeholderColor: colors.inactiveGrey,
      placeholderColorDisabled: colors.inactiveGrey,
      textColor: colors.gray900,
      textColorDisabled: colors.gray500,
      backgroundColorDisabled: colors.gray50,
      backgroundColorHover: colors.gray50,
      labelColor: colors.lighterBlack,
    },
    pill: { border: `none` },
    inviteModal: { linkColor: AS.primary },
  }),

  AC: deepMerge(baseTheme, {
    navigation: {
      backgroundColor: colors.gray950,
      color: colors.white,
      li: {
        default: { color: colors.choiceGray100 },
        hover: { boxShadow: '0px 0px 0px 4px ' + AC.navHoverShadow, color: colors.vividRed },
        active: { color: colors.vividRed, backgroundColor: colors.darkBlack },
        disabled: { color: colors.inactiveGrey },
      },
      profileAvatar: {
        backgroundColor: colors.choiceGray600,
        color: colors.white,
        border: '1px solid ' + colors.gray500,
      },
      companyName: { color: colors.gray400 },
      mobile: { backgroundColor: colors.gray950 },
    },
    buttons: {
      primary: {
        color: colors.white,
        backgroundColor: AC.primary,
        border: '2px solid' + AC.primary,
        hover: { color: colors.white, backgroundColor: AC.primaryHover, border: '2px solid' + AC.primary },
      },
      secondary: {
        color: AC.primary,
        backgroundColor: AC.primaryLight,
        border: '1px solid' + colors.gray300,
        hover: { color: colors.lighterBlack, backgroundColor: colors.gray50 },
      },
      default: {
        color: colors.gray700,
        border: '1px solid' + colors.gray300,
        hover: { color: colors.gray700, backgroundColor: colors.gray50 },
      },
      disabled: {
        color: colors.white,
        backgroundColor: `${AC.primaryDisabled} !important`,
        border: `1px solid transparent !important`,
      },
      badge: { backgroundColor: colors.grayBlue50, color: AC.primary },
      defaultLink: { color: colors.deepBlue },
      disabledIcon: { color: colors.choiceIconGray },
    },
    tabs: {
      color: colors.gray500,
      activeColor: colors.gray700,
      borderBottom: '2px solid ' + colors.gray700,
      titleColor: colors.choiceGray900,
      descriptionColor: colors.choiceGray600,
      containerTitleColor: colors.gray700,
      containerDescriptionColor: colors.choiceGray600,
      numberBadge: { backgroundColor: colors.grayBlue50, color: AC.primary },
    },
    spinners: {
      general: colors.choiceGray600,
      primary: colors.white,
      default: colors.white,
      danger: colors.white,
    },
    colors: { primary: AC.primary },
    modal: {
      backgroundColor: AC.primary,
      outline: '2px solid ' + AC.primary,
      boxShadow: '0 0 0 0.25rem ' + AC.focusRing,
      optionHover: { border: '1px solid ' + AC.primary, backgroundColor: AC.primaryLight },
      selectedOption: {
        text: { color: colors.gray700 },
        backgroundColor: AC.selectedBg,
        border: '2px solid ' + AC.primary,
        hoverBackgroundColor: AC.primary,
        icon: { stroke: AC.primary, fill: colors.grayBlue100 },
      },
    },
    toggle: {
      color: colors.gray700,
      checked: {
        backgroundColor: `${AC.toggleActive} !important`,
        hover: { backgroundColor: `${AC.toggleActiveHover} !important` },
      },
    },
    flatpickr: {
      selectedDate: { border: '1px solid ' + colors.gray500 },
      today: { after: { default: { backgroundColor: AC.primary } } },
      selected: { backgroundColor: AC.primary },
    },
    tooltip: {
      styleOne: {
        hover: { backgroundColor: colors.lightGrey, pathBackgroundColor: colors.grey },
      },
      styleTwo: {
        backgroundColor: colors.grayBlue50,
        color: AC.primary,
        pathBackgroundColor: AC.primary,
        hover: { backgroundColor: colors.grayBlue100, pathBackgroundColor: AC.primaryHover },
      },
    },
    progress: { accentColor: AC.primary, backgroundColor: colors.gray200 },
    navigationIcon: { stroke: colors.white, hover: { stroke: colors.vividRed } },
    icon: {
      stroke: colors.gray700,
      border: '1px solid' + colors.gray250,
      borderRadius: '6px',
      boxShadow: 'none',
      backgroundColor: colors.white,
      checkIcon: `url(${checkIcon})`,
    },
    text: { themeColor: AC.primary },
    discounts: {
      tierLogo: {
        backgroundColor: AC.primaryLight,
        color: AC.primary,
        borderColor: AC.primaryLighter,
      },
      progressBar: { color: AC.primary },
    },
    uploadModal: {
      backgroundImage: `url(${featuredIconAS})`,
      linkColor: AS.primaryHover
    }, //todo: adjust this
    pendingInvitation: {
      backgroundColor: colors.gray50,
      color: colors.gray700,
      checkIconColor: colors.grey,
      buttons: {
        default: {
          hover: { background: colors.gray50 },
        },
        primary: {
          backgroundColor: AC.primary,
          color: colors.white,
          border: `1px solid ${AC.primary}`,
          hover: {
            background: AC.primaryHover,
            backgroundColor: AC.primaryHover,
            color: colors.white,
            border: `1px solid ${AC.primary}`,
          },
          focus: { background: AC.primary, color: colors.white },
        },
      },
      icon: {
        border: '1px solid' + colors.grayBlue100,
        backgroundColor: colors.grayBlue100,
        stroke: AC.primaryHover,
      },
    },
    checkbox: {
      border: `1px solid ${AC.primary}`,
      backgroundColor: AC.primary,
      iconColor: colors.white,
    },
    field: {
      borderColor: colors.darkGrey,
      borderColorFocus: colors.black,
      borderColorActive: colors.choiceGray600,
      borderColorHover: colors.darkGrey,
      borderColorDisabled: 'rgba(118, 118, 118, 0.3)',
      borderColorMenu: colors.lightGrey,
      placeholderColor: colors.inactiveGrey,
      placeholderColorDisabled: colors.inactiveGrey,
      textColor: colors.gray900,
      textColorDisabled: colors.gray500,
      backgroundColorDisabled: colors.gray50,
      backgroundColorHover: colors.gray50,
      labelColor: colors.lighterBlack,
    },
    pill: { border: `1px solid ${colors.veryLightOrange}` },
    inviteModal: { linkColor: AS.primary },
    privacyOptions: {
      textColor: colors.gray400,
      backgroundColor: colors.gray950,
      backgroundColorHover: colors.gray950,
      textHover: colors.darkBlack,
      borderColor: colors.gray200,
      border: 'none',
    },
  }),
};


const isProd = APP_ENVIRONMENT === "PROD";
const cache = {};

export const getTheme = (provider) => {
  if (isProd && cache[provider]) return cache[provider];
  const theme = deepMerge(baseTheme, themeOverrides[provider]);
  if (isProd) cache[provider] = theme;
  return theme;
};

export default getTheme;
